import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { AxiosError } from 'axios'
import React from 'react'
import toast from 'react-hot-toast'
import { useMutation } from 'react-query'
import { Container } from 'src/api/models'
import { useQueryPaginator } from 'src/components/Table'
import { handleErrorMessage, Notification } from 'src/components/Notification'
import { useForm, BooleanField, MapSelectField, NumberField, QuerySelectField, TextareaField } from 'src/forms'
import { setApiErrors } from 'src/forms/errors'
import { skuit } from 'src/services/skuit'
import * as yup from 'yup'

const useStyles = makeStyles(() => ({
  form: {
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
}))

interface ContainerProps {
  facility: string
  parent: string
  container_type: string //  eslint-disable-line camelcase
  count?: number
}

const containerSchema = yup.object().shape({
  facility: yup.string().label('Facility'),
  parent: yup.string().label('Parent Place'),
  container_type: yup.string().label('Container type'),
  count: yup.number().label('Count'),
})

interface Props {
  open: boolean
  handleClose: () => void
  onSave?: () => void
  update?: boolean
  container?: Container
}

export const ContainerDialog = (props: Props) => {
  const { open, handleClose, onSave, update, container } = props
  const classes = useStyles()

  const { Form, data: formData } = useForm(containerSchema)

  const facilityPaginator = useQueryPaginator('facility-list', skuit.sc.facilities.list)
  const placePaginator = useQueryPaginator('place-list', skuit.sc.places.list, { params: { facilities: container?.facility } })

  const { mutate: createContainer } = useMutation(skuit.sc.containers.create, {
    onSuccess: (containers: Container[]) => {
      toast.success(() => (
        <Notification
          message={`Successfully created container: ${containers.length}!`}
          newObject={containers}
        />
      ))

      onSave?.()
    },

    onError: (error: AxiosError) => {
      handleErrorMessage(error, 'creat', 'Container')
      setApiErrors(formData.setError, error)
    },
  })

  const { mutate: updateContainer } = useMutation(skuit.sc.containers.patch, {
    onSuccess: (updatedContainer) => {
      toast.success(() => (
        <Notification
          message={`Successfully updated container!`}
          newObject={updatedContainer}
        />
      ))

      onSave?.()
      handleClose()
    },
    onError: (error: AxiosError) => {
      handleErrorMessage(error, `${update ? 'updat' : 'creat'}`, 'Container')
      setApiErrors(formData.setError, error)
    },
  })

  const onSubmit = (data: ContainerProps) => {
    if (data.container_type === 'pallet') {
      toast.error(() => (
        <Notification
          message={'Pallets cannot be created this way. To create pallets, go to the pallets list page.'}
        />
      ))
    } else {
      if (update) {
        updateContainer({ guid: container?.guid || '', data })
      } else {
        createContainer(data)
      }
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <Form formProps={{ className: classes.form }} onSubmit={onSubmit}>
        <DialogTitle>Create Container</DialogTitle>
        <DialogContent>
          <QuerySelectField
            name="facility"
            label="Facility"
            paginator={facilityPaginator}
            defaultValue={container?.facility}
            disabled={update}
            onSelected={(facility) => {
              placePaginator.patchFilters({ facilities: facility.guid})
              formData.setValue('place', undefined)
            }}
          />
          <QuerySelectField
            name="place"
            label="Place"
            paginator={placePaginator}
            defaultValue={container?.place}
            disabled={update}
          />
          <MapSelectField
            name="container_type"
            label="Container Type"
            options={{generic: 'Generic', pallet: 'Pallet', picking: 'Picking'}}
            defaultValue={container?.containerType}
            disabled={update}
          />
          <BooleanField
            name="for_shipping"
            label="For Shipping"
            defaultValue={container?.forShipping}
          />
          <TextareaField
            name="internal_notes"
            label="Internal Notes"
            defaultValue={container?.internalNotes}
          />
          <BooleanField
            name="review_needed"
            label="Review Needed"
            defaultValue={container?.reviewNeeded}
          />
          <TextareaField
            name="review_reason"
            label="Review Reason"
            defaultValue={container?.reviewReason}
          />
          {!update && (
            <NumberField
              name="count"
              defaultValue={1}
              label="Count"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button type="submit" color="primary" variant="contained" autoFocus>
            Save
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  )
}
